import React, { useMemo, useRef, useState } from 'react';
import {
  Input,
  Button,
  makeStyles,
  shorthands,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import { Search24Regular, Add24Regular } from '@fluentui/react-icons';
import { useProductCatalog } from '../hooks/useProductCatalog';
import { useIsMobile } from '../hooks/useIsMobile';

export interface StockItem {
  id: string;
  identifier: string;
  available: boolean;
  status: string;
  product_id?: string;
  variation_name?: string;
}

export interface ProductGroup {
  id: string;
  name: string;
  sku: string;
  description?: string;
  price?: number;
  trackable?: boolean;
  stockItems: StockItem[];
  availableCount: number;
  totalCount?: number;
  photo_url?: string;
  /** Booqable product_type ('rental' | 'consumable' | 'service'). Carried so the
   *  order page can suppress the shortage notice for services, which have no
   *  tracked stock and therefore can never be short. */
  product_type?: string;
}

interface ProductSearchInputProps {
  onInventoryItemSelect: (productGroup: ProductGroup, stockItem: StockItem) => void;
  onAddProduct?: (productGroup: ProductGroup) => void;
  onAddVariationItem?: (productGroup: ProductGroup, stockItem: StockItem) => void;
  fromDate?: string; // ISO date string for availability check
  tillDate?: string; // ISO date string for availability check
  size?: 'small' | 'medium' | 'large';
  /** Block selection while upstream pricing context (the order's service-address
   *  municipality → rate tier) is still resolving. Without this, a fast user can
   *  add a line before the tier lands and it gets created at $0. Disables the
   *  input and suppresses the dropdown. */
  disabled?: boolean;
  /** Restrict the dropdown to these Booqable product_types (e.g. ['rental']).
   *  When omitted, all types show. Used by the project phase picker, which only
   *  offers rentable bins. */
  allowedProductTypes?: string[];
  /** Render the lightweight native <select> (flat list, no thumbnails, no
   *  search input) on desktop too — the same control mobile already uses.
   *  Used by the project phase picker. */
  native?: boolean;
  /** In the native <select>, show the full product+variation label (e.g.
   *  "Roll-off Dumpster - 10cy") instead of the variation-only label. Used by the
   *  lock dialog, where the user is choosing across products and needs the
   *  product name, not just the size. */
  nativeFullLabel?: boolean;
  /** Controls the native <select>'s current value (an option key = the selected
   *  variation id). When provided, the control reflects the selection instead of
   *  resetting to the "add an action" placeholder — used by the lock dialog, which
   *  shows the chosen default bin in the dropdown itself. Omit to keep the
   *  reset-on-select behavior the phase picker relies on. */
  nativeValue?: string;
  /** Fired when the picker is first activated (focused / opened), before any
   *  selection. The project phase picker uses this to start promoting the
   *  phase's draft order while the user browses, so the ~order-creation latency
   *  overlaps the pick instead of being paid after it. */
  onPickerActivate?: () => void;
}

const useStyles = makeStyles({
  searchInput: {
    width: '100%',
    marginBottom: '16px',
  },
  // Mobile picker: a native <select> so the OS renders the option list (which
  // can't render off-canvas the way Fluent's floating listbox did in this
  // layout). Styled with tokens to sit alongside the Fluent inputs. 16px font
  // keeps iOS from zooming the viewport on focus.
  nativeSelect: {
    width: '100%',
    marginBottom: '16px',
    minHeight: '40px',
    ...shorthands.padding('8px', '12px'),
    fontSize: '16px',
    fontFamily: 'inherit',
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('4px'),
    ':disabled': {
      color: tokens.colorNeutralForegroundDisabled,
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
    },
  },
  productSearchWrapper: {
    position: 'relative',
    width: '100%',
  },
  productDropdown: {
    position: 'absolute',
    top: '100%',
    left: '0',
    right: '0',
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    marginTop: '4px',
    maxHeight: '500px',
    overflowY: 'auto',
    zIndex: 1000,
    // Keep the list scrollable but hide the scrollbar chrome.
    scrollbarWidth: 'none', // Firefox
    msOverflowStyle: 'none', // legacy Edge/IE
    '::-webkit-scrollbar': { display: 'none' }, // Chrome/Safari/Edge
  },
  productGroupItem: {
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  productGroupHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('12px', '16px'),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  productGroupInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  productIcon: {
    width: '40px',
    height: '40px',
    flexShrink: 0,
    objectFit: 'cover',
    ...shorthands.borderRadius('4px'),
  },
  // Blank placeholder for items with no thumbnail — a neutral box rather than
  // the generic front-loader image.
  productIconPlaceholder: {
    width: '40px',
    height: '40px',
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('4px'),
  },
  productTitleSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  productName: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  productSku: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  stockCountText: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  expandIcon: {
    color: tokens.colorNeutralForeground3,
  },
  variationsContainer: {
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.padding('12px', '16px', '12px', '68px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  variationItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('8px', '12px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  variationInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  variationName: {
    fontSize: '13px',
    fontWeight: '500',
    color: tokens.colorNeutralForeground1,
  },
  availabilityText: {
    fontSize: '12px',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  availableText: {
    color: tokens.colorPaletteLightGreenForeground2,
  },
  unavailableText: {
    color: tokens.colorPaletteRedForeground2,
  },
  loadingText: {
    color: tokens.colorNeutralForeground3,
  },
  addButton: {
    minWidth: 'auto',
    height: '28px',
    ...shorthands.padding('0', '10px'),
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ...shorthands.padding('8px', '12px'),
  },
  // Section header ("Items" / "Services") within the flattened dropdown.
  // Solid fill + bold, full-strength text so the section breaks read clearly.
  sectionHeader: {
    ...shorthands.padding('8px', '16px'),
    fontSize: '12px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  // Separator between the Items and Services sections.
  sectionDivider: {
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke1,
  },
});

// Local alias so the rest of this file's references are stable; the real
// shape lives in the shared catalog hook.
type SimpleProductGroup = import('../hooks/useProductCatalog').CatalogProductGroup;

// Orders items within the Items section: roll-offs, then front loaders, then
// bulldog bags, then anything else. Name-based, mirroring classifyCategory in
// portal/productLabels.ts (kept local so this shared admin component doesn't
// import portal code).
function itemCategoryRank(name: string): number {
  const lower = (name || '').toLowerCase();
  if (lower.includes('roll off') || lower.includes('rolloff') || lower.includes('roll-off')) return 0;
  if (lower.includes('front') && lower.includes('loader')) return 1;
  if (lower.includes('bulldog')) return 2;
  return 3;
}

// Native dropdown shows just the variation (e.g. "20 yard"). Variation names are
// fully qualified ("Roll Off 20 yard"), so the group-name prefix is redundant in
// a picker that's already scoped to bins. Strips the prefix; falls back to the
// full label when the variation isn't prefixed with its group name.
function variationOnlyLabel(groupName: string, label: string): string {
  const g = (groupName || '').trim();
  if (g && label.toLowerCase().startsWith(g.toLowerCase())) {
    const rest = label.slice(g.length).replace(/^[\s—,-]+/, '').trim();
    if (rest) return rest;
  }
  return label;
}

// Full bin label = product name + variation, without doubling the product name.
// Booqable variation names are often already prefixed with the product name
// ("Roll-off Dumpster - 10cy"), so a naive `${group}, ${variation}` doubles it
// ("Roll-off Dumpster, Roll-off Dumpster - 10cy"). When the variation already
// leads with the group name, use it as-is; otherwise join them.
export function fullBinLabel(groupName: string, variationName?: string | null): string {
  const g = (groupName || '').trim();
  const v = (variationName || '').trim();
  if (!v) return g;
  if (g && v.toLowerCase().startsWith(g.toLowerCase())) return v;
  return g ? `${g}, ${v}` : v;
}

export const ProductSearchInput: React.FC<ProductSearchInputProps> = ({
  onInventoryItemSelect,
  onAddProduct,
  onAddVariationItem,
  fromDate,
  tillDate,
  size = 'medium',
  disabled = false,
  allowedProductTypes,
  native = false,
  nativeFullLabel = false,
  nativeValue,
  onPickerActivate,
}) => {
  const styles = useStyles();
  const isMobile = useIsMobile();
  const { productGroups, isLoading: loading } = useProductCatalog({ fromDate, tillDate });
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  // The desktop search Input — blurred after a selection so the picker drops
  // focus (dropdown can't linger or be re-triggered by a stray keystroke).
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleProductSearchChange = (value: string) => {
    setProductSearch(value);
    setShowProductDropdown(true);
  };

  const handleAddVariation = (
    productGroup: SimpleProductGroup,
    variation: import('../hooks/useProductCatalog').CatalogVariation,
  ) => {
    // Shortage is WARN-AND-ALLOW (#2): even a fully-booked window (available
    // === 0) is addable. The row's N/N-available count signals the shortage and
    // the order page surfaces a white/orange MessageBar after the add; we never block.
    const pg: ProductGroup = {
      id: productGroup.id,
      name: productGroup.name,
      sku: productGroup.sku,
      description: productGroup.description,
      trackable: productGroup.trackable,
      stockItems: [],
      availableCount: variation.available ?? variation.stock_count ?? 0,
      totalCount: variation.total ?? variation.stock_count ?? 0,
      photo_url: productGroup.photo_url,
      product_type: productGroup.product_type,
    };

    const stockItem: StockItem = {
      id: variation.id,
      identifier: variation.name,
      available: (variation.available ?? variation.stock_count ?? 0) > 0,
      status:
        (variation.available ?? variation.stock_count ?? 0) > 0
          ? 'available'
          : 'unavailable',
      product_id: variation.id,
      variation_name: variation.name,
    };

    if (onAddVariationItem) {
      onAddVariationItem(pg, stockItem);
    } else {
      onInventoryItemSelect(pg, stockItem);
    }
    setShowProductDropdown(false);
    setProductSearch('');
    // Drop focus from the desktop search input so the picker can't be
    // re-triggered by a stray keystroke right after adding. (No-op on the
    // native <select> path, where the ref is unattached.)
    searchInputRef.current?.blur();
  };

  // Flattened, alphabetized list of every addable item for the mobile dropdown:
  // one row per variation (so multi-variation groups are spread out rather than
  // drilled into). Variation names are already fully qualified (e.g. "Front
  // Loader 4 yard"), so use them as-is; single-variation products fall back to
  // the group name. Out-of-stock trackable items are disabled.
  const flatOptions = useMemo(() => {
    const rows = productGroups
      .filter(
        (group) =>
          !allowedProductTypes || allowedProductTypes.includes(group.product_type ?? ''),
      )
      .flatMap((group) =>
      (group.variations ?? []).map((variation) => {
        const hasAvailability = variation.available !== undefined;
        const availableCount = hasAvailability ? (variation.available as number) : variation.stock_count;
        // Shortage is WARN-AND-ALLOW (#2): when a rental window is set, a
        // fully-booked variation (available === 0) is still addable — the row
        // stays enabled (its N/N-available count shows the shortage) instead of
        // going disabled. Only the window-less case (genuinely 0 physical stock)
        // stays disabled.
        const disabled = !hasAvailability && !!group.trackable && (variation.stock_count ?? 0) <= 0;
        const label = group.has_variations ? variation.name : group.name;
        return { key: variation.id, label, group, variation, disabled };
      }),
    );
    rows.sort((a, b) => a.label.localeCompare(b.label));
    return rows;
  }, [productGroups, allowedProductTypes]);

  // Desktop dropdown rows: the same flattened one-row-per-variation list, but
  // search-filtered. Flattening means every addable item shows directly — no
  // group drill-down. Services are included (they're real taxable products).
  // Empty search + open dropdown shows the whole catalog (the list scrolls).
  // SKU is still matched here even though it isn't shown in the row.
  const filteredFlatOptions = useMemo(() => {
    if (!productSearch) {
      return showProductDropdown ? flatOptions : [];
    }
    const s = productSearch.toLowerCase();
    return flatOptions.filter((row) =>
      row.label.toLowerCase().includes(s) ||
      row.group.sku.toLowerCase().includes(s) ||
      (!!row.group.description && row.group.description.toLowerCase().includes(s)) ||
      row.variation.name.toLowerCase().includes(s)
    );
  }, [flatOptions, productSearch, showProductDropdown]);

  // Split the dropdown into two sections: Items (rentals + sale items, ordered
  // roll-off → front loader → bulldog bag → other) and Services. Services are
  // identified by Booqable's product_type; everything else is an Item.
  const { itemRows, serviceRows } = useMemo(() => {
    const items = filteredFlatOptions
      .filter((r) => r.group.product_type !== 'service')
      .sort(
        (a, b) =>
          itemCategoryRank(a.group.name) - itemCategoryRank(b.group.name) ||
          a.label.localeCompare(b.label),
      );
    const services = filteredFlatOptions
      .filter((r) => r.group.product_type === 'service')
      .sort((a, b) => a.label.localeCompare(b.label));
    return { itemRows: items, serviceRows: services };
  }, [filteredFlatOptions]);

  // Native <select> — flat text list, no thumbnails, no group drill-down, no
  // search box. Used on mobile (the OS renders the option list off-canvas) and
  // wherever `native` is requested (the project phase picker). Kept unselected
  // (controlled value="") so it reads as an "add an item" action; re-selecting
  // the same product fires onChange again because the value resets to "".
  if (isMobile || native) {
    return (
      <div className={styles.productSearchWrapper}>
        <select
          className={styles.nativeSelect}
          aria-label="Add a product"
          value={nativeValue ?? ''}
          disabled={loading || disabled || flatOptions.length === 0}
          // Warm up promotion the moment the picker is focused (before the user
          // picks), so the order-creation latency overlaps their browsing.
          onFocus={() => onPickerActivate?.()}
          // A mouse wheel over a focused <select> cycles its options and
          // silently re-fires onChange (adding a stray line). Blur on wheel so
          // the scroll scrolls the page instead. (We intentionally do NOT blur
          // on selection — that forced a second click to reopen the picker.)
          onWheel={(e) => e.currentTarget.blur()}
          onChange={(e) => {
            const row = flatOptions.find((r) => r.key === e.target.value);
            if (row && !row.disabled) {
              handleAddVariation(row.group, row.variation);
            }
          }}
        >
          <option value="" disabled>
            {loading ? 'Loading products…' : 'Add a product'}
          </option>
          {flatOptions.map((row) => {
            const optLabel = nativeFullLabel
              ? fullBinLabel(row.group.name, row.group.has_variations ? row.variation.name : undefined)
              : row.group.has_variations
                ? variationOnlyLabel(row.group.name, row.label)
                : row.label;
            return (
              <option key={row.key} value={row.key} disabled={row.disabled}>
                {row.disabled ? `${optLabel} (out of stock)` : optLabel}
              </option>
            );
          })}
        </select>
      </div>
    );
  }

  // One flattened dropdown row. Shared by the Items and Services sections.
  const renderRow = (row: typeof flatOptions[number]) => {
    const { group, variation, label, disabled } = row;
    // Window-aware availability is only present when the catalog was fetched
    // with a date range; otherwise fall back to raw stock.
    const hasAvailability = variation.available !== undefined;
    const availableCount = hasAvailability
      ? (variation.available as number)
      : variation.stock_count;
    const totalCount = variation.total ?? variation.stock_count;

    return (
      <div key={row.key} className={styles.productGroupItem}>
        <div
          className={styles.productGroupHeader}
          // Whole row is the add hitspot — no drill-down.
          onClick={() => { if (!disabled) handleAddVariation(group, variation); }}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          <div className={styles.productGroupInfo}>
            {group.photo_url ? (
              <img src={group.photo_url} alt={label} className={styles.productIcon} />
            ) : (
              <div className={styles.productIconPlaceholder} aria-hidden />
            )}
            <div className={styles.productTitleSection}>
              <div className={styles.productName}>{label}</div>
            </div>
          </div>
          <div className={styles.productGroupInfo}>
            {hasAvailability ? (
              <div
                className={`${styles.availabilityText} ${
                  availableCount > 0 ? styles.availableText : styles.unavailableText
                }`}
              >
                {availableCount} / {totalCount} available
              </div>
            ) : group.trackable ? (
              <div className={styles.stockCountText}>
                {variation.stock_count} in stock
              </div>
            ) : null}
            <Button
              appearance="primary"
              size="small"
              icon={<Add24Regular />}
              className={styles.addButton}
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) handleAddVariation(group, variation);
              }}
              disabled={disabled}
            >
              Add
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.productSearchWrapper}>
      <Input
        className={styles.searchInput}
        input={{ ref: searchInputRef }}
        size={size}
        disabled={disabled}
        contentBefore={<Search24Regular />}
        contentAfter={loading ? <Spinner size="tiny" /> : undefined}
        placeholder="Search products by name or SKU"
        value={productSearch}
        onChange={(e, data) => handleProductSearchChange(data.value)}
        onFocus={() => {
          if (disabled) return;
          onPickerActivate?.();
          setShowProductDropdown(true);
        }}
        onBlur={() => {
          setTimeout(() => setShowProductDropdown(false), 200);
        }}
      />
      {!disabled && showProductDropdown && (itemRows.length > 0 || serviceRows.length > 0) && (
        <div
          className={styles.productDropdown}
          // Prevent the search input from blurring when clicking inside the
          // dropdown — without this, a row click triggers the input's onBlur
          // which dismisses the dropdown 200ms later.
          onMouseDown={(e) => e.preventDefault()}
        >
          {itemRows.length > 0 && (
            <>
              <div className={styles.sectionHeader}>Items</div>
              {itemRows.map(renderRow)}
            </>
          )}
          {serviceRows.length > 0 && (
            <>
              {itemRows.length > 0 && <div className={styles.sectionDivider} />}
              <div className={styles.sectionHeader}>Services</div>
              {serviceRows.map(renderRow)}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductSearchInput;
